import { useState, useEffect, useRef } from "react";
import { CommentItem } from "pages/api/discuss/debate/[discussId]";
import { TYPO } from "styles/Typo";
import CommentDeleteModal from "components/modals/CommentDeleteModal";
import { CommnetComponentsType } from "types/main";
import {
  deleteOpinion,
  deleteOpinionComment,
  reportOpinionComment,
} from "utils/clientAPIs/debate";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useDebateContext } from "contexts/DebateContext";
import { useDebateDetailContext } from "contexts/DebateDetailContext";
import { useMainCommentListContext } from "../../../MainCommentList";
import CustomToast from "components/atoms/CustomToast";
import * as Sentry from "@sentry/nextjs";
import { useDiscussIdContext } from "contexts/DiscussIdContext";
function MoreButton({
  data,
  type,
  setMode,
}: {
  data: CommentItem;
  type: CommnetComponentsType;
  setMode: (mode: "read" | "edit") => void;
}) {
  const { mutate, triggerMutate, removeTopRef, voteId } = useDebateContext();
  const debateDetailContext = useDebateDetailContext();
  const mainCommentListContext = useMainCommentListContext();

  const { data: session }: any = useSession();
  const router = useRouter();
  const { discussId } = useDiscussIdContext();
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isMyComment = data?.is_my_comment;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [successReportToast, setSuccessReportToast] = useState(false);

  // 화면에서 modalRef 를 제외한 나머지 영역을 클릭했을 때 모달을 닫음
  const handleClickOutside = (event: MouseEvent) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(event.target as Node) &&
      !buttonRef.current?.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
  }, []);

  // 신고하기 콜백
  const handleReport = async (e: any) => {
    e.stopPropagation();
    setIsOpen(false);
    console.log("신고하기");
    // if (!voteId) return;

    if (!session?.user?.email) {
      router.push(`/login?from=${router.asPath}`);
      return;
    }

    try {
      await reportOpinionComment(data.comment_id);
      setSuccessReportToast(true);
    } catch (error) {
      console.error(error);
    }
  };

  // 수정하기 콜백
  const handleEdit = (e: any) => {
    e.stopPropagation();
    setIsOpen(false);
    setMode("edit");
  };

  // 삭제하기 콜백
  const handleDelete = (e: any) => {
    e.stopPropagation();
    setIsOpen(false);
    setIsDeleteModalOpen(true);
  };

  const submitDelete = async (myEmail?: string) => {
    if (!myEmail) return;
    if (myEmail !== data.email) return;
    const sleep = new Promise((resolve) => setTimeout(resolve, 150));

    try {
      if (type === "opinion") {
        await deleteOpinion(myEmail, data.comment_id);
        await sleep;
        mutate();
      } else if (type === "optionDetail") {
        await deleteOpinion(myEmail, data.comment_id);
        router.push(`/discuss/${discussId}`);
      } else {
        if (!data.parent_id) return;
        await deleteOpinionComment(myEmail, data.parent_id, data.comment_id);
        await sleep;
        mutate();
        removeTopRef(data.comment_id);
        if (
          router.pathname.startsWith("/discuss/[slugId]/opinion") &&
          debateDetailContext
        )
          debateDetailContext.detailMutate();
        else {
          if (mainCommentListContext && (data as any)?.isNewComment) {
            mainCommentListContext.setAllComments((prev: any) => {
              return prev.filter(
                (item: any) => item.comment_id !== data.comment_id
              );
            });
          } else triggerMutate(data.parent_id);
        }
      }
    } catch (error) {
      Sentry.captureMessage(
        `[Debate] ${type === "comment" ? "댓글" : "의견"} 삭제 오류`,
        {
          level: "error",
          tags: {
            errorType: `[Debate] ${
              type === "comment" ? "댓글" : "의견"
            } 삭제 오류`,
          },
          extra: { data: error },
        }
      );
    }
  };

  return (
    <div className="cursor-pointer relative debate-more-button p-1">
      <div
        data-test-id="more-button"
        ref={buttonRef}
        onClick={(e: any) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <MoreIcon />
      </div>
      <div
        className={`z-[10] rounded-[16px] absolute top-[20px] right-0 w-[160px] flex flex-col gap-1 py-1 bg-white ${
          isOpen ? "block" : "hidden"
        }`}
        ref={modalRef}
        style={{
          boxShadow: " 0px 0px 16px 0px rgba(0, 0, 0, 0.16)",
          WebkitBoxShadow: " 0px 0px 16px 0px rgba(0, 0, 0, 0.16)",
        }}
      >
        {isMyComment ? (
          <>
            <div
              data-test-id="edit-button"
              className="px-3 py-2 flex items-center gap-2 cursor-pointer"
              onClick={handleEdit}
            >
              <EditIcon />
              <span className={`${TYPO.TYPO5_1_MD} text-gray70`}>수정하기</span>
            </div>
            <div
              data-test-id="delete-button"
              className="px-3 py-2 flex items-center gap-2 cursor-pointer"
              onClick={handleDelete}
            >
              <DeleteIcon />
              <span className={`${TYPO.TYPO5_1_MD} text-gray70`}>삭제하기</span>
            </div>
          </>
        ) : (
          <div
            data-test-id="report-button"
            className="px-3 py-2 flex items-center gap-2 cursor-pointer"
            onClick={handleReport}
          >
            <ReportIcon />
            <span className={`${TYPO.TYPO5_1_MD} text-gray70`}>신고하기</span>
          </div>
        )}
      </div>
      {isDeleteModalOpen && (
        <CommentDeleteModal
          setModal={setIsDeleteModalOpen}
          type={type}
          deleteHandler={() => {
            submitDelete(session?.user?.email);
          }}
        />
      )}
      <CustomToast
        isSuccess={true}
        message={"신고가 완료되었어요"}
        isVisible={successReportToast}
        onClose={() => setSuccessReportToast(false)}
        successIconFill={"#34BB7A"}
        width={220}
      />
    </div>
  );
}

const ReportIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <rect width="24" height="24" fill="white" />
      <rect x="2" y="17" width="20" height="3" rx="1" fill="#D9D9D9" />
      <path
        d="M5.38392 5.75569C5.5073 4.75316 6.35885 4 7.36894 4H16.6311C17.6412 4 18.4927 4.75316 18.6161 5.75569L20 17H4L5.38392 5.75569Z"
        fill="var(--red40)"
      />
      <circle cx="12" cy="11" r="4" fill="var(--red50)" />
    </svg>
  );
};

const DeleteIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.7125 16.7125C10.5208 16.9042 10.2833 17 10 17C9.71667 17 9.47917 16.9042 9.2875 16.7125C9.09583 16.5208 9 16.2833 9 16V9C9 8.71667 9.09583 8.47917 9.2875 8.2875C9.47917 8.09583 9.71667 8 10 8C10.2833 8 10.5208 8.09583 10.7125 8.2875C10.9042 8.47917 11 8.71667 11 9V16C11 16.2833 10.9042 16.5208 10.7125 16.7125ZM14.7125 16.7125C14.5208 16.9042 14.2833 17 14 17C13.7167 17 13.4792 16.9042 13.2875 16.7125C13.0958 16.5208 13 16.2833 13 16V9C13 8.71667 13.0958 8.47917 13.2875 8.2875C13.4792 8.09583 13.7167 8 14 8C14.2833 8 14.5208 8.09583 14.7125 8.2875C14.9042 8.47917 15 8.71667 15 9V16C15 16.2833 14.9042 16.5208 14.7125 16.7125Z"
        fill="var(--gray50)"
      />
      <path
        d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6C4.71667 6 4.47917 5.90417 4.2875 5.7125C4.09583 5.52083 4 5.28333 4 5C4 4.71667 4.09583 4.47917 4.2875 4.2875C4.47917 4.09583 4.71667 4 5 4H9C9 3.71667 9.09583 3.47917 9.2875 3.2875C9.47917 3.09583 9.71667 3 10 3H14C14.2833 3 14.5208 3.09583 14.7125 3.2875C14.9042 3.47917 15 3.71667 15 4H19C19.2833 4 19.5208 4.09583 19.7125 4.2875C19.9042 4.47917 20 4.71667 20 5C20 5.28333 19.9042 5.52083 19.7125 5.7125C19.5208 5.90417 19.2833 6 19 6V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM10 17C10.2833 17 10.5208 16.9042 10.7125 16.7125C10.9042 16.5208 11 16.2833 11 16V9C11 8.71667 10.9042 8.47917 10.7125 8.2875C10.5208 8.09583 10.2833 8 10 8C9.71667 8 9.47917 8.09583 9.2875 8.2875C9.09583 8.47917 9 8.71667 9 9V16C9 16.2833 9.09583 16.5208 9.2875 16.7125C9.47917 16.9042 9.71667 17 10 17ZM14 17C14.2833 17 14.5208 16.9042 14.7125 16.7125C14.9042 16.5208 15 16.2833 15 16V9C15 8.71667 14.9042 8.47917 14.7125 8.2875C14.5208 8.09583 14.2833 8 14 8C13.7167 8 13.4792 8.09583 13.2875 8.2875C13.0958 8.47917 13 8.71667 13 9V16C13 16.2833 13.0958 16.5208 13.2875 16.7125C13.4792 16.9042 13.7167 17 14 17Z"
        fill="var(--gray30)"
      />
    </svg>
  );
};

const EditIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M18.6108 9.19052L14.8254 5.39298L4.85739 15.393L8.6428 19.1905L18.6108 9.19052Z"
        fill="#3F3F3F"
      />
      <path
        d="M18.6108 9.19052L14.8254 5.39298L4.85739 15.393L8.6428 19.1905L18.6108 9.19052Z"
        fill="#F3AE3D"
      />
      <path
        d="M8.6428 19.1905L4.85739 15.393L3.5095 20.0585C3.45043 20.3144 3.67605 20.5443 3.93238 20.4886L8.6428 19.1905Z"
        fill="#F7CEAD"
      />
      <path
        d="M5.75858 19.9862L4.03397 18.2442L3.5095 20.0585C3.45043 20.3144 3.67605 20.5443 3.93238 20.4886L5.75858 19.985V19.9862Z"
        fill="#333D4B"
      />
      <path
        d="M18.0742 3.8555L20.1448 5.93275C20.6184 6.40786 20.6184 7.17877 20.1448 7.65388L18.6129 9.19067L14.8275 5.39312L16.3594 3.85633C16.833 3.38122 17.6014 3.38122 18.075 3.85633L18.0742 3.8555Z"
        fill="#F09636"
      />
    </svg>
  );
};

const MoreIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <mask
        id="mask0_20451_1730"
        // style="mask-type:alpha"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="24"
        height="24"
      >
        <rect width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_20451_1730)">
        <path
          d="M6 14C5.45 14 4.97917 13.8042 4.5875 13.4125C4.19583 13.0208 4 12.55 4 12C4 11.45 4.19583 10.9792 4.5875 10.5875C4.97917 10.1958 5.45 10 6 10C6.55 10 7.02083 10.1958 7.4125 10.5875C7.80417 10.9792 8 11.45 8 12C8 12.55 7.80417 13.0208 7.4125 13.4125C7.02083 13.8042 6.55 14 6 14ZM12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.55 13.8042 13.0208 13.4125 13.4125C13.0208 13.8042 12.55 14 12 14ZM18 14C17.45 14 16.9792 13.8042 16.5875 13.4125C16.1958 13.0208 16 12.55 16 12C16 11.45 16.1958 10.9792 16.5875 10.5875C16.9792 10.1958 17.45 10 18 10C18.55 10 19.0208 10.1958 19.4125 10.5875C19.8042 10.9792 20 11.45 20 12C20 12.55 19.8042 13.0208 19.4125 13.4125C19.0208 13.8042 18.55 14 18 14Z"
          fill="var(--gray30)"
        />
      </g>
    </svg>
  );
};

export default MoreButton;
